import React, { createContext, useEffect, useReducer } from 'react';

// third-party
import { jwtDecode } from 'jwt-decode';

// reducer - state management
import { FORCE_LOGIN, LOGIN, LOGOUT } from 'store/actions';
import accountReducer from 'store/accountReducer';

// project imports
import Loader from 'ui-component/Loader';
import axios from 'utils/axios';

// import { useNavigate } from 'react-router-dom';

// types
import { KeyedObject } from 'types';
import { InitialLoginContextProps, JWTContextType } from 'types/auth';

//socket
import Sockets from 'services/SocketService';
import { updateModelAttributes } from 'services/SocketModelUpdate';
import ReCAPTCHA from 'react-google-recaptcha';

// constant
const initialState: InitialLoginContextProps = {
    isLoggedIn: false,
    isInitialized: false,
    user: null,
    isForceLogin: false
};

const verifyToken: (st: string) => boolean = (serviceToken) => {
    if (!serviceToken) {
        return false;
    }
    const decoded: KeyedObject = jwtDecode(serviceToken);
    /**
     * Property 'exp' does not exist on type '<T = unknown>(token: string, options?: JwtDecodeOptions | undefined) => T'.
     */
    // return true;

    return decoded.exp > Date.now() / 1000;
};

const setSession = (serviceToken?: string | null) => {
    if (serviceToken) {
        localStorage.setItem('serviceToken', serviceToken);
        axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    } else {
        localStorage.removeItem('serviceToken');
        delete axios.defaults.headers.common.Authorization;
    }
};

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //
const JWTContext = createContext<JWTContextType | null>(null);

export const JWTProvider = ({ children }: { children: React.ReactElement }) => {
    const [state, dispatch] = useReducer(accountReducer, initialState);
    // const navigate = useNavigate();
    useEffect(() => {
        const init = async () => {
            try {
                const serviceToken = window.localStorage.getItem('serviceToken');

                if (serviceToken) {
                    setSession(serviceToken);
                    const response = await axios.get('/user');
                    // Sockets.setSocketForUser(response.data.body.userId, updateModelAttributes);
                    dispatch({
                        type: LOGIN,
                        payload: {
                            isLoggedIn: true,
                            user: response.data,
                            isForceLogin: false
                        }
                    });
                } else {
                    dispatch({
                        type: LOGOUT
                    });
                    // navigate('/');
                }
            } catch (err) {
                console.error(err);
                dispatch({
                    type: LOGOUT
                });
            }
        };

        init();
    }, []);

    const removeCookieData = (name: string) => {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
    };

    const login = async (username: string, password: any) => {
        try {
            const skipCaptcha = true;
            const response = await axios.post(`/signin/phone`, { username, password, skipCaptcha });
            const body = response.data.body.response;
            const formatedResponse = {
                ...body,
                refreshToken: body.refresh_token,
                serviceToken: body.access_token,
                token: body.access_token,
                userId: body.UserId
            };
            const { token, userId } = formatedResponse;

            setSession(token);
            // Sockets.setSocketForUser(body.userId, updateModelAttributes);
            removeCookieData('age_consent');
            dispatch({
                type: LOGIN,
                payload: {
                    isLoggedIn: true,
                    user: body,
                    isForceLogin: false
                }
            });
            return;
        } catch (error) {
            console.log(error);
            return { error: error?.response?.data.message };
        }

        // location.reload();
    };

    const forceLogin = async (status: boolean) => {
        // Sockets.setSocketForUser(response.data.id, updateModelAttributes);
        dispatch({
            type: FORCE_LOGIN,
            payload: {
                isForceLogin: status
            }
        });
    };

    const signupOtp = async (
        phone: string,
        email: string,
        countrycode: string,
        userCaptcha: String | null | ReCAPTCHA,
        loginMode: string,
        referenceCode?: string | undefined
    ) => {
        const temp = `${countrycode}${phone}`;
        console.log(userCaptcha);
        const skipCaptcha = userCaptcha === '' ? true : false;
        const response = await axios.post(
            `/signup/${loginMode}/otp`,
            loginMode === 'phone' ? { phone: temp, referenceCode, userCaptcha, skipCaptcha } : { email, userCaptcha, skipCaptcha }
        );
        return response.data;
    };

    const setForgotPassword = async (
        phone: string,
        email: string,
        password: string,
        passwordConfirmation: string,
        token: string,
        phoneOtp: String
    ) => {
        const response = await axios.post(`/user/password/forgot/reset`, {
            phone,
            email,
            password,
            otp: phoneOtp,
            confirm_pass: passwordConfirmation,
            token
        });
        return response.data.body;
    };

    const forgotPasswordOtp = async (phone: string, email: string, password: string, userCaptcha: String | null | ReCAPTCHA) => {
        const response = await axios.post(`/user/password/forgot`, {
            phone,
            email,
            password,
            skipCaptcha: true
        });
        return response.data.body;
    };
    const updatePassword = async (newpassword: string, password: string) => {
        const response = await axios.post(`/user/password/change`, {
            newpassword,
            password
        });
        return response.data.body;
    };

    const register = async (
        firstName: string,
        lastName: string,
        phone: string,
        email: string,
        password: string,
        countrycode: string,
        token: string,
        phoneOtp: string,
        loginMode: string,
        referenceCode?: string | undefined,
        //selfieWithID is file upload field
        selfieWithID?: File | null,
        idUpload?: File | null,
        dob?: string | null | undefined
    ) => {
        const temp = `${countrycode}${phone}`;

        const values = {
            firstName,
            lastName,
            referenceUserId: referenceCode ?? '',
            countryCode: countrycode,
            phone: temp,
            password,
            ageConsent: true,
            token,
            otp: phoneOtp,
            selfieWithID,
            idUpload,
            dob
        };

        const formData = new FormData();

        for (const key in values) {
            const value = values[key as keyof typeof values];

            if (value !== undefined && value !== null) {
                // ✅ Check for array of files
                if (key === 'selfieWithID' || key === 'idUpload') {
                    if (Array.isArray(value)) {
                        // If it's an array, append each File
                        value.forEach((file) => {
                            if (file instanceof File || file instanceof Blob) {
                                formData.append(key, file); // allow multiple files if needed
                            }
                        });
                    } else if (value instanceof File || value instanceof Blob) {
                        // Single file
                        formData.append(key, value);
                    } else {
                        console.warn(`${key} is not a valid File or File[]`, value);
                    }
                } else {
                    // All non-file fields
                    formData.append(key, value as any);
                }
            }
        }

        const response = await axios.post(
            `/signup/${loginMode}/register`,
            loginMode === 'phone'
                ? formData
                : { firstName, lastName, email, password, token, otp: phoneOtp, ageConsent: true, selfieWithID, idUpload },
            {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            }
        );
        return response.data.body;
        // dispatch({
        //     type: REGISTER,
        //     payload: {
        //         isLoggedIn: false,
        //         user: response.data
        //     }
        // });

        // let users = response.data;

        // if (window.localStorage.getItem('users') !== undefined && window.localStorage.getItem('users') !== null) {
        //     const localUsers = window.localStorage.getItem('users');
        //     users = [
        //         ...JSON.parse(localUsers!),
        //         {
        //             id,
        //             email,
        //             password
        //             //name: `${firstName} ${lastName}`
        //         }
        //     ];
        // }

        // window.localStorage.setItem('users', JSON.stringify(users));
    };

    const logout = () => {
        // Disconnect the socket
        if (Sockets.instance) {
            Sockets.instance.unsubscribe();
        }
        setSession(null);
        dispatch({ type: LOGOUT });
    };

    const resetPassword = async (email: string) => {};

    const updateProfile = () => {};

    if (state.isInitialized !== undefined && !state.isInitialized) {
        return <Loader />;
    }

    return (
        <JWTContext.Provider
            value={{
                ...state,
                login,
                logout,
                signupOtp,
                forgotPasswordOtp,
                setForgotPassword,
                register,
                resetPassword,
                updateProfile,
                forceLogin,
                updatePassword
            }}
        >
            {children}
        </JWTContext.Provider>
    );
};

export default JWTContext;
